import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/site";
import { Button } from "@/components/ui/button";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/75 backdrop-blur-2xl border-b border-white/10 py-3"
          : "bg-gradient-to-b from-black/60 via-black/30 to-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gold/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <img
              src={BRAND.logo}
              alt="Star Otogaz – İnci Akü"
              className="relative h-12 sm:h-14 w-auto object-contain"
              loading="eager"
            />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-extrabold text-lg tracking-tight text-white">
              STAR OTOGAZ
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold">
              İnci Akü Yetkili Bayi
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-gold"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-gold to-transparent transition-opacity ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BRAND.phoneTel}`}
            data-testid="header-call-btn"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-gold transition-colors"
          >
            <Phone className="w-4 h-4" />
            {BRAND.phone}
          </a>
          <Link to="/iletisim" className="hidden sm:block">
            <Button
              data-testid="header-quote-btn"
              className="gradient-gold text-black font-semibold rounded-none px-5 py-2 h-auto hover:shadow-[0_0_24px_rgba(212,175,55,0.5)] hover:opacity-95 transition-all"
            >
              Teklif Al
            </Button>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menü"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-2xl border-t border-white/10 animate-fade-up"
        >
          <nav className="flex flex-col px-6 py-6 gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 px-4 text-base font-medium border-l-2 transition-all ${
                    isActive
                      ? "text-gold border-gold bg-gold/5"
                      : "text-white/85 border-transparent hover:border-white/30 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="mt-3 py-3 px-4 text-base font-medium text-white inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4 text-gold" /> {BRAND.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
